import { Box, Stack, Typography } from "@mui/material";
import { MetricGroup } from "./metricsApi";
import {
  computeBounds,
  formatTimeLabel,
  plotSeries,
  yAxisTicks,
} from "./chartGeometry";

// The SVG scales to the card width, so the viewBox ratio sets the rendered height: at a ~1500px
// card this is roughly 300px tall, matching V2's wide, shallow band. A squarer viewBox looked
// correct in isolation but grew to ~480px once stretched across the card.
const WIDTH = 1600;
const HEIGHT = 330;
const PADDING = { left: 62, right: 20, top: 16, bottom: 44 };
// Type sizes are in viewBox units, so they shrink with the same scale factor as the drawing.
const AXIS_FONT = 17;

/**
 * One metric family as a multi-series line chart, one line per resource, with a legend showing
 * each resource's latest value. Drawn as inline SVG on a viewBox so it scales with the card
 * rather than needing a resize observer.
 */
export default function MetricChart({ group }: { group: MetricGroup }) {
  const bounds = computeBounds(group.series);

  if (!bounds) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
        No data points in this window.
      </Typography>
    );
  }

  const plotted = plotSeries(group.series, bounds, WIDTH, HEIGHT, PADDING);
  const ticks = yAxisTicks(bounds);
  const span = bounds.maxTime - bounds.minTime;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;

  return (
    <Box>
      <Box
        component="svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`${group.metricName} over time`}
        sx={{ width: "100%", height: "auto", display: "block" }}
      >
        {/* V2 frames the plot with an axis on the left and the baseline only, not a full box. */}
        <line
          x1={PADDING.left}
          x2={PADDING.left}
          y1={PADDING.top}
          y2={PADDING.top + plotHeight}
          stroke="#D8DCE2"
          strokeWidth={1.5}
        />
        <line
          x1={PADDING.left}
          x2={WIDTH - PADDING.right}
          y1={PADDING.top + plotHeight}
          y2={PADDING.top + plotHeight}
          stroke="#D8DCE2"
          strokeWidth={1.5}
        />

        {ticks.map((value, i) => {
          const y = PADDING.top + (i / (ticks.length - 1)) * plotHeight;
          return (
            <g key={value}>
              <line
                x1={PADDING.left}
                x2={WIDTH - PADDING.right}
                y1={y}
                y2={y}
                stroke="#EEF0F3"
                strokeWidth={1}
              />
              <text
                x={PADDING.left - 10}
                y={y + 6}
                textAnchor="end"
                fontSize={AXIS_FONT}
                fill="#6B7280"
              >
                {Number.isInteger(value) ? value : value.toFixed(1)}
              </text>
            </g>
          );
        })}

        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
          const x =
            PADDING.left + fraction * (WIDTH - PADDING.left - PADDING.right);
          return (
            <text
              key={fraction}
              x={x}
              y={HEIGHT - 12}
              textAnchor="middle"
              fontSize={AXIS_FONT}
              fill="#6B7280"
            >
              {formatTimeLabel(bounds.minTime + fraction * span, span)}
            </text>
          );
        })}

        {plotted.map((s) => (
          <g key={s.id}>
            {s.area && (
              <polygon
                points={s.area}
                fill={s.color}
                fillOpacity={plotted.length > 3 ? 0.06 : 0.14}
                stroke="none"
              />
            )}
            <polyline
              points={s.points}
              fill="none"
              stroke={s.color}
              strokeWidth={2.2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </g>
        ))}
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 1 }}>
        {plotted.map((s) => (
          <Stack key={s.id} direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                backgroundColor: s.color,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {s.resource}
              {s.lastValue != null && ` (${s.lastValue.toFixed(1)})`}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
